import type { Point } from "@bedrock/canvas";
import type { CanvasItem } from "@bedrock/domain";

export const CANVAS_ROTATION_SNAP_DEGREES = 15;

export function normalizeCanvasRotation(rotation: number): number {
  const normalized = ((rotation % 360) + 360) % 360;
  const signed = normalized > 180 ? normalized - 360 : normalized;
  return Object.is(signed, -0) ? 0 : signed;
}

export function canvasRotationPointerAngle(center: Point, pointer: Point): number {
  const angle = Math.atan2(pointer.y - center.y, pointer.x - center.x) * 180 / Math.PI;
  return normalizeCanvasRotation(angle + 90);
}

export function shortestCanvasRotationDelta(previousAngle: number, nextAngle: number): number {
  return normalizeCanvasRotation(nextAngle - previousAngle);
}

export function updateCanvasRotation(
  accumulatedRotation: number,
  previousPointerAngle: number,
  center: Point,
  pointer: Point,
  snap = false
): { accumulatedRotation: number; pointerAngle: number; rotation: number } {
  const pointerAngle = canvasRotationPointerAngle(center, pointer);
  const nextAccumulatedRotation = accumulatedRotation + shortestCanvasRotationDelta(previousPointerAngle, pointerAngle);
  const rotation = snap
    ? Math.round(nextAccumulatedRotation / CANVAS_ROTATION_SNAP_DEGREES) * CANVAS_ROTATION_SNAP_DEGREES
    : nextAccumulatedRotation;

  return {
    accumulatedRotation: nextAccumulatedRotation,
    pointerAngle,
    rotation: normalizeCanvasRotation(rotation)
  };
}

export function rotateCanvasFrameItems(
  beforeFrame: CanvasItem,
  afterFrame: CanvasItem,
  items: CanvasItem[]
): CanvasItem[] {
  if (beforeFrame.props.kind !== "frame" || afterFrame.id !== beforeFrame.id) return [afterFrame];

  const rotatingIds = new Set([beforeFrame.id]);
  let addedDescendant = true;
  while (addedDescendant) {
    addedDescendant = false;
    for (const item of items) {
      if (item.parentStackId && rotatingIds.has(item.parentStackId) && !rotatingIds.has(item.id)) {
        rotatingIds.add(item.id);
        addedDescendant = true;
      }
    }
  }

  const rotationDelta = normalizeCanvasRotation(afterFrame.rotation - beforeFrame.rotation);
  const radians = rotationDelta * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const frameCenter = {
    x: beforeFrame.x + beforeFrame.width / 2,
    y: beforeFrame.y + beforeFrame.height / 2
  };

  return items.filter((item) => rotatingIds.has(item.id)).map((item) => {
    if (item.id === beforeFrame.id) return afterFrame;
    const itemCenter = { x: item.x + item.width / 2, y: item.y + item.height / 2 };
    const offset = { x: itemCenter.x - frameCenter.x, y: itemCenter.y - frameCenter.y };
    const rotatedCenter = {
      x: frameCenter.x + offset.x * cosine - offset.y * sine,
      y: frameCenter.y + offset.x * sine + offset.y * cosine
    };
    return {
      ...item,
      x: rotatedCenter.x - item.width / 2,
      y: rotatedCenter.y - item.height / 2,
      rotation: normalizeCanvasRotation(item.rotation + rotationDelta)
    };
  });
}
